import React from 'react';
import { NavLink, Link } from 'react-router-dom';
import { ReactComponent as Logo } from '../../logo.svg';
import { useV2Translation } from '../i18n';
import { V2_LANDING } from '../nav';
import { V2_MICRO_LINKS } from '../routes';
import { NavItemView, NavSectionView, useV2Nav } from '../useV2Nav';
import { NAV_ICONS, LockIcon, SearchIcon } from './icons';
import { cx, FOCUS_RING_SURFACE } from './styles';

/**
 * The 256px rail (DESIGN_PLAN §2.3–2.7). Nine items in three sections plus a
 * footer zone, section labels that are not clickable and not collapsible, and
 * feature-gated items shown as locked rather than hidden.
 *
 * `SidebarView` is the pure presentational half: it reads no hooks, so the
 * landing page's `ProductMock` can render the real rail from static data
 * without firing a single request. `Sidebar` is the live wrapper.
 */

const ITEM_BASE =
  'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all';
const ITEM_RESTING = 'text-white/60 hover:bg-white/[0.05] hover:text-white';
const ITEM_ACTIVE =
  'text-[#3b82f6] bg-[#3b82f6]/10 before:content-[""] before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-r before:bg-[#3b82f6]';
const ITEM_LOCKED = 'text-white/35 hover:bg-white/[0.03] hover:text-white/50';

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="px-3 pb-2 pt-5 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/30">
    {children}
  </p>
);

interface ItemProps {
  item: NavItemView;
  /** Plan chip on `Profile`. */
  chip?: string | null;
  /** Static mode: `activePath` decides the active item, nothing is a link. */
  activePath?: string;
  isStatic?: boolean;
  drawer?: boolean;
  onNavigate?: () => void;
}

const ItemBody: React.FC<{ item: NavItemView; chip?: string | null; active: boolean }> = ({
  item,
  chip,
  active,
}) => {
  const Icon = NAV_ICONS[item.icon];
  return (
    <>
      <Icon
        className={cx(
          'h-[18px] w-[18px] flex-shrink-0',
          active ? 'text-[#3b82f6]' : item.locked ? 'text-white/20' : 'text-white/40',
        )}
      />
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {item.beta && (
        <span className="flex-shrink-0 rounded border border-[#3b82f6]/25 bg-[#3b82f6]/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#60a5fa]">
          Beta
        </span>
      )}
      {item.locked && (
        <span className="flex flex-shrink-0 items-center gap-1.5">
          <LockIcon className="h-3 w-3 text-white/25" />
          {item.tierChip && (
            <span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-[10px] text-white/40">
              {item.tierChip}
            </span>
          )}
        </span>
      )}
      {item.attention !== undefined && (
        <span className="flex flex-shrink-0 items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
          <span className="text-[10px] tabular-nums text-amber-400/80">{item.attention}</span>
        </span>
      )}
      {chip && (
        <span className="flex-shrink-0 rounded bg-[#3b82f6]/15 px-1.5 py-0.5 text-[10px] font-semibold text-[#60a5fa]">
          {chip}
        </span>
      )}
    </>
  );
};

const SidebarItem: React.FC<ItemProps> = ({
  item,
  chip,
  activePath,
  isStatic,
  drawer,
  onNavigate,
}) => {
  const sizing = cx(drawer && 'min-h-[44px]');

  if (isStatic) {
    const active = activePath === item.path;
    return (
      <div
        className={cx(ITEM_BASE, sizing, active ? ITEM_ACTIVE : item.locked ? ITEM_LOCKED : ITEM_RESTING)}
      >
        <ItemBody item={item} chip={chip} active={active} />
      </div>
    );
  }

  return (
    <NavLink
      to={item.path}
      title={item.title}
      data-tour={`nav-${item.id}`}
      onClick={onNavigate}
      className={({ isActive }) =>
        cx(
          ITEM_BASE,
          sizing,
          FOCUS_RING_SURFACE,
          isActive ? ITEM_ACTIVE : item.locked ? ITEM_LOCKED : ITEM_RESTING,
        )
      }
    >
      {({ isActive }) => <ItemBody item={item} chip={chip} active={isActive} />}
    </NavLink>
  );
};

export interface SidebarViewProps {
  sections: NavSectionView[];
  planLabel?: string | null;
  /** Accessible name for the `<nav>`. */
  navLabel: string;
  searchLabel: string;
  microLinks?: readonly { labelKey: string; path: string }[];
  /** Localiser for the micro-links. Omitted in static mode. */
  t?: (key: string) => string;
  onSearchClick?: () => void;
  onNavigate?: () => void;
  /** Static mode for the landing mock: no NavLinks, no router dependency. */
  isStatic?: boolean;
  activePath?: string;
  drawer?: boolean;
}

export const SidebarView: React.FC<SidebarViewProps> = ({
  sections,
  planLabel,
  navLabel,
  searchLabel,
  microLinks,
  t,
  onSearchClick,
  onNavigate,
  isStatic,
  activePath,
  drawer,
}) => {
  const mainSections = sections.filter((s) => s.id !== 'account');
  const footerSection = sections.find((s) => s.id === 'account');

  const brand = (
    <div className="flex h-16 flex-shrink-0 items-center gap-3 border-b border-white/[0.06] px-4">
      <Logo style={{ width: '32px', height: '32px' }} />
      <span className="text-lg font-bold tracking-tight text-[#3b82f6]">FlashTools</span>
    </div>
  );

  return (
    <div className="flex h-full flex-col bg-[#0f0f0f]">
      {isStatic ? (
        brand
      ) : (
        <Link to={V2_LANDING} className={cx('block', FOCUS_RING_SURFACE)} onClick={onNavigate}>
          {brand}
        </Link>
      )}

      {/* Command palette trigger */}
      <div className="flex-shrink-0 px-3 pt-3">
        {isStatic ? (
          <div className="flex w-full items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-sm text-white/30">
            <SearchIcon className="h-4 w-4 flex-shrink-0" />
            <span className="flex-1 text-left">{searchLabel}</span>
            <kbd className="rounded border border-white/10 px-1.5 py-0.5 text-[10px] text-white/30">
              ⌘K
            </kbd>
          </div>
        ) : (
          <button
            type="button"
            onClick={onSearchClick}
            data-tour="search"
            className={cx(
              'flex w-full items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-sm text-white/40 transition-colors hover:border-white/[0.14] hover:text-white/60',
              drawer && 'min-h-[44px]',
              FOCUS_RING_SURFACE,
            )}
          >
            <SearchIcon className="h-4 w-4 flex-shrink-0" />
            <span className="flex-1 text-left">{searchLabel}</span>
            <kbd className="rounded border border-white/10 px-1.5 py-0.5 text-[10px] text-white/30">
              ⌘K
            </kbd>
          </button>
        )}
      </div>

      <nav aria-label={navLabel} className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 pb-4">
        {mainSections.map((section) => (
          <div key={section.id}>
            <SectionLabel>{section.label}</SectionLabel>
            <div className="space-y-0.5">
              {section.items.map((item) => (
                <SidebarItem
                  key={item.id}
                  item={item}
                  activePath={activePath}
                  isStatic={isStatic}
                  drawer={drawer}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          </div>
        ))}

        <div className="flex-1" aria-hidden="true" />

        {footerSection && (
          <div className="mt-4 border-t border-white/[0.06]">
            <SectionLabel>{footerSection.label}</SectionLabel>
            <div className="space-y-0.5">
              {footerSection.items.map((item) => (
                <SidebarItem
                  key={item.id}
                  item={item}
                  chip={item.id === 'profile' ? planLabel : undefined}
                  activePath={activePath}
                  isStatic={isStatic}
                  drawer={drawer}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          </div>
        )}

        {microLinks && microLinks.length > 0 && (
          <div className="flex items-center gap-2 px-3 pt-4 text-[11px] text-white/25">
            {microLinks.map((link, i) => (
              <React.Fragment key={link.path}>
                {i > 0 && <span aria-hidden="true">·</span>}
                {isStatic ? (
                  <span>{t ? t(link.labelKey) : link.labelKey}</span>
                ) : (
                  <Link
                    to={link.path}
                    onClick={onNavigate}
                    className={cx('rounded transition-colors hover:text-white/50', FOCUS_RING_SURFACE)}
                  >
                    {t ? t(link.labelKey) : link.labelKey}
                  </Link>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </nav>
    </div>
  );
};

/** Live rail / drawer contents. Identical in both — never a reduced set. */
export const Sidebar: React.FC<{
  onSearchClick?: () => void;
  onNavigate?: () => void;
  drawer?: boolean;
}> = ({ onSearchClick, onNavigate, drawer }) => {
  const { t } = useV2Translation();
  const { sections, planLabel } = useV2Nav();

  return (
    <SidebarView
      sections={sections}
      planLabel={planLabel}
      navLabel={t('app.navigation.mainLabel')}
      searchLabel={t('app.navigation.search')}
      microLinks={V2_MICRO_LINKS}
      t={t}
      onSearchClick={onSearchClick}
      onNavigate={onNavigate}
      drawer={drawer}
    />
  );
};
